import { Box, Paper, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useCallback, useMemo, useState } from "react";
import {
	AXIS_HELP,
	colorForIndex,
	isPlottable,
	SPEED_AXIS_MAX,
	SPEED_AXIS_MIN,
	TIMER_AXIS_MAX,
	TIMER_AXIS_MIN,
	type TimerSpeedPoint,
} from "../models/TimerSpeedReport";

interface Props {
	points: TimerSpeedPoint[];
}

// ECharts fires mouse events for an axis name only when the axis carries
// `triggerEvent`, and it draws to canvas, so the name cannot hold a title
// attribute or an MUI Tooltip. The event gives viewport coordinates; the panel
// is positioned from those.
interface AxisEventParams {
	componentType?: string;
	targetType?: string;
	event?: { event?: { clientX: number; clientY: number } };
}

interface HelpPanel {
	text: string;
	x: number;
	y: number;
}

export const TimerSpeedScatter = ({ points }: Props) => {
	const [help, setHelp] = useState<HelpPanel | null>(null);

	const option = useMemo(() => {
		const byProduct = new Map<string, number[][]>();

		for (const point of points) {
			if (!isPlottable(point)) continue;
			const data = byProduct.get(point.ProductName);
			if (data) data.push([point.Timer, point.Speed as number]);
			else
				byProduct.set(point.ProductName, [
					[point.Timer, point.Speed as number],
				]);
		}

		// Busiest product first, so the ten legacy colours land on the series an
		// operator actually looks for and the colour of a product does not move
		// every time a rare one appears in the range.
		const ordered = [...byProduct.entries()].sort(
			(a, b) => b[1].length - a[1].length,
		);

		const series = ordered.map(([name, data], index) => ({
			name,
			type: "scatter",
			data,
			symbolSize: 4,
			// Without this the browser lays out one DOM-backed shape per point and
			// a four-month range (~72k points) locks the tab.
			large: true,
			largeThreshold: 2000,
			itemStyle: { color: colorForIndex(index) },
		}));

		return {
			animation: false,
			tooltip: {
				trigger: "item",
				// Values only. What each one measures is on the axis name, one hover
				// away; repeating it on every point is noise.
				formatter: (params: { seriesName: string; value: [number, number] }) =>
					`<b>${params.seriesName}</b><br/>` +
					`Timer: ${params.value[0].toLocaleString()} ms<br/>` +
					`Speed: ${params.value[1].toFixed(2)}`,
			},
			// The legacy draws its legend inside the plot area, over the points. With
			// 133 products that is unreadable, so it moves out and scrolls.
			legend: { type: "scroll", bottom: 0, textStyle: { fontSize: 11 } },
			grid: { left: 70, right: 30, top: 16, bottom: 62 },
			xAxis: {
				type: "value",
				// The unit is verifiable — DATEDIFF(MILLISECOND, …) — so it goes on
				// the axis. Speed keeps the legacy's bare label: the historian tag
				// ST1_SPEEDVALUE carries no Unit, so printing one would be a guess.
				name: "Timer (ms)",
				nameLocation: "middle",
				nameGap: 26,
				triggerEvent: true,
				min: TIMER_AXIS_MIN,
				max: TIMER_AXIS_MAX,
				interval: 5000,
				axisLabel: { fontSize: 11 },
			},
			yAxis: {
				type: "value",
				name: "Speed",
				nameLocation: "middle",
				nameGap: 44,
				triggerEvent: true,
				min: SPEED_AXIS_MIN,
				max: SPEED_AXIS_MAX,
				interval: 5,
				axisLabel: { fontSize: 11 },
			},
			series,
		};
	}, [points]);

	const handleAxisOver = useCallback((params: AxisEventParams) => {
		if (params?.targetType !== "axisName") return;
		const axis = params.componentType === "xAxis" ? "Timer" : "Speed";
		const native = params.event?.event;
		setHelp({
			text: AXIS_HELP[axis],
			x: native?.clientX ?? 0,
			y: native?.clientY ?? 0,
		});
	}, []);

	const handleAxisOut = useCallback((params: AxisEventParams) => {
		if (params?.targetType !== "axisName") return;
		setHelp(null);
	}, []);

	const onEvents = useMemo(
		() => ({ mouseover: handleAxisOver, mouseout: handleAxisOut }),
		[handleAxisOver, handleAxisOut],
	);

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<ReactECharts
				option={option}
				notMerge
				lazyUpdate
				onEvents={onEvents}
				style={{ height: "100%", width: "100%" }}
			/>
			{help && (
				<Paper
					elevation={4}
					sx={{
						position: "fixed",
						// Above and slightly left of the pointer, clamped so a name near
						// the right edge does not push the panel off screen.
						left: Math.min(help.x + 12, window.innerWidth - 340),
						top: Math.max(help.y - 84, 8),
						zIndex: (theme) => theme.zIndex.tooltip,
						maxWidth: 320,
						px: 1.25,
						py: 0.75,
						pointerEvents: "none",
						bgcolor: "rgba(38,38,38,0.94)",
					}}
				>
					<Typography variant="caption" sx={{ color: "#fff", lineHeight: 1.4 }}>
						{help.text}
					</Typography>
				</Paper>
			)}
		</Box>
	);
};
